import { Component, OnInit } from '@angular/core';
import { AuthorizationService } from './authorization.service';
import { LoginService } from '../login/login.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

interface UserData {
  name: string;
  email: string;
  phone: string;
}

interface PaymentResponse {
  status: string;
  data?: {
    checkoutUrl?: string;
  };
}

@Component({
  selector: 'app-authorization',
  templateUrl: './authorization.component.html',
  styleUrls: ['./authorization.component.scss'],
})
export class AuthorizationComponent implements OnInit {
  showReceiptPopup = false;

  userData: UserData | null = null;
  source = '';
  isProcessing = false;

  // CRITICAL FIX: Increased delays to ensure localStorage is ready
  private readonly INIT_DELAY = 300; // Increased from 100ms to 300ms
  private readonly MAX_RETRIES = 3; // Retry loading user data
  private readonly RETRY_DELAY = 500; // Wait 500ms between retries

  private readonly BACKUP_TIMEOUT = 1000;
  private readonly AUTH_KEYS = [
    'token',
    'authToken', // CRITICAL: Check both token keys
    'loginResponse',
    'otpResponse',
    'tokenType',
    'userName',
    'userPhone',
    'usermail',
  ];

  constructor(
    private authService: AuthorizationService,
    private loginService: LoginService,
    private toaster: ToastrService,
    private router: Router
  ) {}

  ngOnInit(): void {
    console.log('🚀 AuthorizationComponent ngOnInit started');
    console.log('🔍 Initial localStorage state:', {
      token: !!localStorage.getItem('token'),
      authToken: !!localStorage.getItem('authToken'),
      usermail: !!localStorage.getItem('usermail'),
      userName: !!localStorage.getItem('userName'),
      userPhone: !!localStorage.getItem('userPhone'),
    });

    // CRITICAL FIX: Use retry logic to handle race conditions
    setTimeout(() => {
      this.initializeUserDataWithRetry(0);
    }, this.INIT_DELAY);
  }

  /**
   * CRITICAL FIX: Retry loading user data multiple times before giving up
   */
  private initializeUserDataWithRetry(attempt: number): void {
    console.log(`🔄 Attempt ${attempt + 1}/${this.MAX_RETRIES} to load user data`);

    this.initializeUserData();

    // If user data wasn't loaded and we have retries left, try again
    if (!this.userData && attempt < this.MAX_RETRIES - 1) {
      console.log(`⏳ Retry in ${this.RETRY_DELAY}ms...`);
      setTimeout(() => {
        this.initializeUserDataWithRetry(attempt + 1);
      }, this.RETRY_DELAY);
    } else if (!this.userData) {
      console.error(`❌ Failed to load user data after ${this.MAX_RETRIES} attempts`);
      this.handleMissingUserData();
    }
  }

  private initializeUserData(): void {
    console.log('🔄 Initializing user data in AuthorizationComponent');
    
    // CRITICAL: Log ALL localStorage keys for debugging
    const allKeys = Object.keys(localStorage);
    console.log('🔍 All localStorage keys:', allKeys);
    console.log('🔍 localStorage contents:', {
      token: localStorage.getItem('token')?.substring(0, 20) + '...',
      authToken: localStorage.getItem('authToken')?.substring(0, 20) + '...',
      usermail: localStorage.getItem('usermail'),
      userName: localStorage.getItem('userName'),
      userPhone: localStorage.getItem('userPhone'),
      tokenType: localStorage.getItem('tokenType'),
    });

    // Attempt to recover authentication data if needed
    this.authService.recoverAuthenticationData();

    // CRITICAL FIX: Try multiple sources in priority order
    const userData = 
      this.getUserDataFromLocalStorage() || // NEW: Direct localStorage check first
      this.getUserDataFromOtpResponse() || 
      this.getUserDataFromLogin();

    if (userData) {
      this.userData = userData.data;
      this.source = userData.source;
      console.log('✅ User data loaded successfully from:', userData.source);
      console.log('✅ User data:', this.userData);
    } else {
      console.warn('⚠️ No user data found in this attempt');
    }
  }

  /**
   * NEW METHOD: Directly read from localStorage without going through service
   * This bypasses any service caching issues
   */
  private getUserDataFromLocalStorage(): { data: UserData; source: string } | null {
    const email = localStorage.getItem('usermail');
    const name = localStorage.getItem('userName');
    const phone = localStorage.getItem('userPhone');

    console.log('🔍 Direct localStorage check:', { email, name, phone });

    if (email && name) {
      console.log('✅ Found complete user data in localStorage');
      return {
        data: {
          email,
          name,
          phone: phone || '',
        },
        source: 'Direct localStorage',
      };
    }

    console.log('⚠️ Incomplete user data in localStorage');
    return null;
  }

  private handleMissingUserData(): void {
    // CRITICAL FIX: Check ALL possible token locations
    const hasToken = 
      !!localStorage.getItem('token') || 
      !!localStorage.getItem('authToken') ||
      !!sessionStorage.getItem('authToken');
    
    console.log('🔍 handleMissingUserData - hasToken:', hasToken);

    if (hasToken) {
      // We have a token but no user data - try to construct minimal data from localStorage
      const email = localStorage.getItem('usermail');
      const name = localStorage.getItem('userName');
      const phone = localStorage.getItem('userPhone');
      
      console.log('🔍 Attempting recovery with:', { email, name, phone });

      if (email && name) {
        console.log('✅ Recovered user data from localStorage');
        this.userData = { email, name, phone: phone || '' };
        this.source = 'Recovered Session';
        return;
      }

      // CRITICAL FIX: If we have token but missing user data, 
      // this might be a legitimate login that just hasn't finished storing data
      console.warn('⚠️ Have token but missing user data - this might resolve itself');
      this.toaster.warning(
        'Loading user data...',
        'Please wait',
        { timeOut: 2000 }
      );
      return;
    }
    
    // No token and no user data - definitely need to login
    console.error('❌ No valid authentication found. Redirecting to login.');
    this.toaster.error(
      'Session data not found. Please login again.',
      'Authentication Required',
      { timeOut: 3000 }
    );
    
    // Delay redirect to allow user to see the error
    setTimeout(() => {
      this.router.navigate(['/login']);
    }, 3000);
  }

  private getUserDataFromOtpResponse(): {
    data: UserData;
    source: string;
  } | null {
    const otpResponse = this.authService.getOtpResponse();

    if (this.isValidOtpResponse(otpResponse)) {
      return {
        data: {
          name: otpResponse.name,
          email: otpResponse.email,
          phone: otpResponse.phoneNumber,
        },
        source: 'OTP Verification',
      };
    }

    return null;
  }

  private getUserDataFromLogin(): { data: UserData; source: string } | null {
    const loginData = this.loginService.getUserData();

    console.log('🔍 getUserDataFromLogin - loginData:', loginData);

    if (this.isValidLoginData(loginData)) {
      console.log('✅ Valid login data found');
      return {
        data: {
          name: loginData.name,
          email: loginData.email,
          phone: loginData.phone,
        },
        source: 'Login Service',
      };
    }

    console.warn('⚠️ Invalid login data:', loginData);
    return null;
  }

  private isValidOtpResponse(otpResponse: any): otpResponse is { name: string; email: string; phoneNumber: string } {
    return otpResponse && 
           typeof otpResponse.name === 'string' && 
           typeof otpResponse.email === 'string' && 
           typeof otpResponse.phoneNumber === 'string';
  }

  private isValidLoginData(loginData: any): boolean {
    const isValid = loginData && loginData.name && loginData.email && loginData.phone;
    console.log('🔍 isValidLoginData:', { loginData, isValid });
    return isValid;
  }

  onAuthorizePayment(): void {
    console.log('💳 Authorizing payment for:', this.userData);

    if (this.isProcessing) {
      console.log('⚠️ Payment already processing, ignoring click');
      return;
    }

    // CRITICAL CHECK: Verify authentication before proceeding
    if (!this.authService.isAuthenticated()) {
      console.error('❌ Not authenticated - token missing or expired');
      this.handleAuthenticationError();
      return;
    }

    // CRITICAL FIX: Check ALL possible token locations
    const token = 
      localStorage.getItem('token') || 
      localStorage.getItem('authToken') ||
      sessionStorage.getItem('authToken');
      
    if (!token) {
      console.error('❌ Token not found in any storage location');
      this.handleAuthenticationError();
      return;
    }

    console.log('✅ Authentication verified, showing receipt popup');
    console.log('✅ Token found in:', this.getTokenLocation());
    // Show popup instead of processing payment immediately
    this.showReceiptPopup = true;
  }

  /**
   * NEW METHOD: Identify which storage location has the token
   */
  private getTokenLocation(): string {
    if (localStorage.getItem('token')) return 'localStorage.token';
    if (localStorage.getItem('authToken')) return 'localStorage.authToken';
    if (sessionStorage.getItem('authToken')) return 'sessionStorage.authToken';
    return 'none';
  }

  private handleAuthenticationError(): void {
    this.toaster.error('Authentication token not found. Please login again.', 'Authentication Error');
    setTimeout(() => {
      this.router.navigateByUrl('/login');
    }, 1500);
  }

  private processPayment(): void {
    console.log('💳 Processing payment...');
    
    // Backup authentication data before making payment request
    this.backupAuthenticationData();

    this.authService.makePayment().subscribe({
      next: (response: PaymentResponse) => {
        this.handlePaymentSuccess(response);
      },
      error: (error) => {
        this.handlePaymentError(error);
      },
    });
  }

  // Process payment only after popup is closed
  closePopup(): void {
    console.log('✅ Receipt reminder acknowledged, processing payment');
    this.showReceiptPopup = false;

    // Now process the actual payment
    this.isProcessing = true;
    this.processPayment();
  }

  private handlePaymentSuccess(response: PaymentResponse): void {
    this.isProcessing = false;
    console.log('✅ Payment response received:', response);

    if (this.isSuccessfulPaymentResponse(response)) {
      this.processSuccessfulPayment(response.data!.checkoutUrl!);
    } else {
      console.error('❌ Payment initialization failed:', response);
      this.toaster.error('Payment initialization failed. Please try again.', 'Payment Error');
    }
  }

  private isSuccessfulPaymentResponse(response: PaymentResponse): boolean {
    return response.status === 'Success' && !!response.data && !!response.data.checkoutUrl;
  }

  private processSuccessfulPayment(checkoutUrl: string): void {
    console.log('✅ Payment initialized successfully, redirecting to:', checkoutUrl);
    this.toaster.success('Payment initialized successfully!', 'Success');

    const checkoutId = this.extractCheckoutId(checkoutUrl);

    if (checkoutId) {
      this.saveCheckoutId(checkoutId);
      console.log('💾 CheckoutId extracted and saved:', checkoutId);
    }

    // Backup all auth data to sessionStorage before redirect
    this.backupToSessionStorage();
    
    // Redirect to payment gateway
    window.location.href = checkoutUrl;
  }

  private saveCheckoutId(checkoutId: string): void {
    localStorage.setItem('checkoutId', checkoutId);
    sessionStorage.setItem('checkoutId', checkoutId);
  }

  private handlePaymentError(error: any): void {
    this.isProcessing = false;
    console.error('❌ Payment error:', error);

    const errorHandlers: { [key: number]: () => void } = {
      401: () => this.handleUnauthorizedError(),
      400: () => this.handleBadRequestError(),
      500: () => this.handleServerError(),
    };

    const handler = errorHandlers[error.status];
    if (handler) {
      handler();
    } else {
      this.handleGenericPaymentError(error);
    }
  }

  private handleUnauthorizedError(): void {
    console.error('❌ 401 Unauthorized - session expired');
    this.toaster.error('Session expired. Please login again.', 'Authentication Error');
    setTimeout(() => {
      this.router.navigateByUrl('/login');
    }, 1500);
  }

  private handleBadRequestError(): void {
    this.toaster.error('Invalid payment data. Please check your information.', 'Payment Error');
  }

  private handleServerError(): void {
    this.toaster.error('Server error. Please try again later.', 'Server Error');
  }

  private handleGenericPaymentError(error?: any): void {
    const message = error?.message || 'Payment failed. Please try again.';
    this.toaster.error(message, 'Payment Error');
  }

  private backupAuthenticationData(): void {
    console.log('💾 Backing up authentication data...');
    
    const currentToken = this.authService.getCurrentToken();
    const otpResponse = this.authService.getOtpResponse();

    if (currentToken) {
      sessionStorage.setItem('backup_token', currentToken);
      console.log('✅ Token backed up');
    }

    if (otpResponse) {
      sessionStorage.setItem('backup_otpResponse', JSON.stringify(otpResponse));
      console.log('✅ OTP response backed up');
    }

    const loginResponse = localStorage.getItem('loginResponse');
    if (loginResponse) {
      sessionStorage.setItem('backup_loginResponse', loginResponse);
      console.log('✅ Login response backed up');
    }
  }

  private backupToSessionStorage(): void {
    console.log('💾 Backing up all auth keys to sessionStorage...');
    
    let backedUpCount = 0;
    this.AUTH_KEYS.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value) {
        sessionStorage.setItem(`backup_${key}`, value);
        backedUpCount++;
      }
    });
    
    console.log(`✅ Backed up ${backedUpCount} auth keys`);
  }

  private extractCheckoutId(checkoutUrl: string): string | null {
    try {
      // First try the original simple method
      const simpleExtraction = checkoutUrl.split('/').pop();
      if (simpleExtraction) {
        return simpleExtraction;
      }

      // Fallback to URL parsing method
      const url = new URL(checkoutUrl);
      const segments = url.pathname.split('/').filter(Boolean);
      return segments[segments.length - 1] || null;
    } catch (error) {
      console.error('❌ Error extracting checkout ID:', error);
      return null;
    }
  }

  onBack(): void {
    console.log('🔙 User clicked back button');
    setTimeout(() => {
      this.toaster.info('You have logged out!', 'Login Info');
      this.router.navigateByUrl('/login');
    }, this.BACKUP_TIMEOUT);
  }
}